import { $lookAt, $panel, $session, roleIn } from "./state/stores";
import { $activeTool } from "./state/tools";
import { $lookMarker } from "./state/markers";
import { $chatOpen } from "./state/chat";
import { markerAt } from "./engine/markerPins";
import { isRemovable } from "./world/rules";
import { type Commands, type CommandDeps, createCommands, type RoomKeys } from "./input/commands";
import type { WorldSession } from "./session";
import type { PlaceMode } from "./engine/placing";
import type { EntityManager } from "./engine/entities";
import { CLICK_TO_RESUME, type HUD } from "./hud/hud";
import { $touchMode } from "./state/touch";

export type { RoomKeys };

const isTextField = (el: Element | null) => !!el && ["INPUT", "TEXTAREA", "SELECT"].includes(el.tagName);

export interface KeyDeps extends CommandDeps {
  session: WorldSession;
  placing: PlaceMode;
  entities: EntityManager;
  hud: HUD;
  canvas: HTMLCanvasElement;
}

/** Keys and the mouse to commands (spec 2p §3.3); returns the commands for the touch HUD. */
export function bindKeys(ctx: KeyDeps): Commands {
  const cmd = createCommands(ctx);
  $panel.subscribe((p) => {
    if (p) $activeTool.set(null);
    ctx.room.activePlayer().setMovementBlocked(!!p);
    if (p) document.exitPointerLock(); else if (!$touchMode.get()) ctx.hud.setPrompt(CLICK_TO_RESUME);
  });
  window.addEventListener("keydown", (e) => {
    if ($chatOpen.get()) return; // the chat input handles its own keys (spec 2i §3.2)
    if (e.code === "Escape") { cmd.back(); return; }
    // M closes the mailbox, but a letter m typed into a mail is just a letter (Review Focus 3)
    if (e.code === "KeyM" && $panel.get() === "mail" && !isTextField(document.activeElement)) { $panel.set(null); return; }
    if (document.activeElement?.tagName === "INPUT" || $panel.get()) return;
    switch (e.code) {
      case "KeyR": cmd.toggleRoom(); return;
      case "KeyE": cmd.interact(); return;
      case "KeyT": e.preventDefault(); cmd.openChat(); return;
      case "Tab": e.preventDefault(); cmd.openPanel("inventory"); return;
      case "KeyF": cmd.toggleJoin(); return;
      case "KeyP": cmd.openPanel("plot"); return;
      case "KeyM": cmd.openPanel("mail"); return;
    }
    if (/^Digit[1-9]$/.test(e.code)) cmd.selectSlot(Number(e.code.slice(5)) - 1);
  });
  window.addEventListener("mousedown", (e) => { if (e.button === 0 && document.pointerLockElement) cmd.confirm(); });
  window.addEventListener("wheel", (e) => cmd.rotate(e.deltaY > 0 ? 15 : -15));

  // Inspect what the crosshair is on, 10× per second.
  setInterval(() => {
    if (ctx.room.active()) { if ($lookAt.get()) $lookAt.set(null); if ($lookMarker.get()) $lookMarker.set(null); return; } // the room picks its own
    if (ctx.placing.active() || $activeTool.get()) { $lookAt.set(null); $lookMarker.set(null); return; }
    const cam = ctx.session.player.camera;
    const hit = cam.getScene().pickWithRay(cam.getForwardRay(6), (m) => !!m.metadata?.entityId || !!m.metadata?.markerId);
    const mk = markerAt(hit?.pickedMesh);
    if (mk) {
      if ($lookAt.get()) $lookAt.set(null);
      if ($lookMarker.get()?.id !== mk.id) $lookMarker.set(mk);
      return;
    }
    if ($lookMarker.get()) $lookMarker.set(null);
    const ent = ctx.entities.entityAt(hit?.pickedMesh);
    if (!ent) { if ($lookAt.get()) $lookAt.set(null); return; }
    const me = $session.get()?.id;
    const removable = isRemovable(ent, me, roleIn(ent.plotId));
    const look = $lookAt.get();
    if (look?.entityId !== ent.id || look.removable !== removable) $lookAt.set({ entityId: ent.id, removable });
  }, 100);
  return cmd;
}
